/**
 * DataSourceBadge.js
 *
 * Icon-only indicator showing data source (S2P, Database, Manual)
 * Left-click to show dropdown, right-click for context menu
 *
 * Visual: Gain: 20.3 dB 📊
 *
 * Uses progressive disclosure: only shows if multiple sources are available
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import THEME from '../styles/theme';

// Map source to icon and size multiplier
const sourceConfig = {
  's2p': { icon: '𝐒', sizeMultiplier: 1.4 },
  'database': { icon: '📊', sizeMultiplier: 1 },
  'manual': { icon: '✋', sizeMultiplier: 1 }
};

const DataSourceBadge = ({ currentSource, availableSources, onContextMenu, onSourceChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Progressive disclosure: only show if multiple sources available
  if (!availableSources || availableSources.length <= 1) {
    return null;
  }

  const currentConfig = sourceConfig[currentSource] || { icon: '?', sizeMultiplier: 1 };

  const handleClick = (e) => {
    e.stopPropagation();
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.left - 40
      });
    }
    setIsOpen(!isOpen);
  };

  const handleSourceSelect = (source) => {
    if (onSourceChange && source.value !== currentSource) {
      onSourceChange(source.value);
    }
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <SourceIcon
        ref={iconRef}
        title={`Data source: ${currentSource} (click to change)`}
        onClick={handleClick}
        onContextMenu={onContextMenu}
        style={{ fontSize: `${7 * currentConfig.sizeMultiplier}px` }}
      >
        {currentConfig.icon}
      </SourceIcon>
      {isOpen && ReactDOM.createPortal(
        <>
          <Backdrop onClick={handleClose} />
          <Menu style={{ top: dropdownPos.top, left: dropdownPos.left }}>
            <MenuHeader>Data Source</MenuHeader>
            {availableSources.map((source) => (
              <MenuItem
                key={source.value}
                $isActive={source.value === currentSource}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSourceSelect(source);
                }}
              >
                <SourceItemIcon style={{ fontSize: `${14 * (sourceConfig[source.value]?.sizeMultiplier || 1)}px` }}>
                  {sourceConfig[source.value]?.icon || '?'}
                </SourceItemIcon>
                <SourceLabel>{source.label}</SourceLabel>
                {source.value === currentSource && <CheckMark>✓</CheckMark>}
              </MenuItem>
            ))}
          </Menu>
        </>,
        document.body
      )}
    </>
  );
};

const SourceIcon = styled.span`
  font-size: 7px;
  margin-left: 4px;
  opacity: 0.7;
  user-select: none;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${THEME.zIndex.modal - 1};
`;

const Menu = styled.div`
  position: fixed;
  min-width: 140px;
  background: ${THEME.sidebarBackground};
  border: 1px solid ${THEME.borderColor};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: ${THEME.zIndex.modal};
  overflow: hidden;
  animation: slideIn 150ms ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const MenuHeader = styled.div`
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 600;
  color: ${THEME.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${THEME.borderColor};
  background: rgba(0, 0, 0, 0.2);
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  background: ${props => props.$isActive ? 'rgba(220, 38, 38, 0.1)' : 'transparent'};
  border-left: 3px solid ${props => props.$isActive ? THEME.accent : 'transparent'};
  transition: all ${THEME.transitionFast};

  &:hover {
    background: rgba(220, 38, 38, 0.15);
  }

  &:active {
    background: rgba(220, 38, 38, 0.2);
  }
`;

const SourceItemIcon = styled.span`
  font-size: 14px;
  width: 20px;
  text-align: center;
`;

const SourceLabel = styled.span`
  flex: 1;
  font-size: 13px;
  color: ${THEME.textPrimary};
  font-weight: 500;
`;

const CheckMark = styled.span`
  color: ${THEME.accent};
  font-size: 14px;
  font-weight: bold;
`;

export default DataSourceBadge;
